import React, { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './BinanceTicker.css';

// Coin icons mapping - Alternative CDN URLs
const coinIcons = {
    'BTC': 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
    'ETH': 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    'LTC': 'https://assets.coingecko.com/coins/images/2/large/litecoin.png',
    'ADA': 'https://assets.coingecko.com/coins/images/975/large/cardano.png',
    'AVAX': 'https://assets.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png',
    'SOL': 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
    'DOGE': 'https://assets.coingecko.com/coins/images/5/large/dogecoin.png',
    'WIFF': 'https://assets.coingecko.com/coins/images/33566/large/dogwifhat.jpg',
    'VINE': 'https://assets.coingecko.com/coins/images/35062/large/grape.png',
    'TRX': 'https://assets.coingecko.com/coins/images/1094/large/tron-logo.png',
    'XRP': 'https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png',
    'BNB': 'https://assets.coingecko.com/coins/images/825/large/bnb-icon2_2x.png',
    'USDT': 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
    'SHIB': 'https://assets.coingecko.com/coins/images/11939/large/shiba.png',
    'USDC': 'https://assets.coingecko.com/coins/images/6319/large/usdc.png',
    'LINK': 'https://assets.coingecko.com/coins/images/877/large/chainlink-new-logo.png',
    'USDS': 'https://assets.coingecko.com/coins/images/6319/large/usdc.png',
    'XMR': 'https://assets.coingecko.com/coins/images/69/large/monero_logo.png',
    'DOGW': 'https://assets.coingecko.com/coins/images/27029/large/dogewhale-200x200.png',
    'BTT': 'https://assets.coingecko.com/coins/images/12313/large/bittorrent-logo.png'
};

/**
 * Reusable Binance Ticker component
 * Props:
 * - requestedBases: string[] of base assets (e.g. ['BTC','ETH']) to stream against USDT
 * - speedSeconds: number for one full scroll duration (default 40)
 * - direction: 'left' | 'right' - scroll direction (default 'left')
 * - offset: boolean - if true, starts from middle for seamless continuation effect
 */
export default function BinanceTicker({ 
    requestedBases = ['BTC', 'ETH', 'LTC', 'ADA', 'AVAX', 'SOL', 'DOGE', 'WIFF', 'VINE'], 
    speedSeconds = 40,
    direction = 'left',
    offset = false
}) {
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [tickerMap, setTickerMap] = useState({});
    const priceHistoryRef = useRef({}); // symbol -> number[]
    const wsRef = useRef(null);

    const lowercaseStreams = useMemo(() => {
        return availableSymbols.map((s) => `${s.toLowerCase()}@ticker`);
    }, [availableSymbols]);

    useEffect(() => {
        let cancelled = false;
        async function fetchExchangeInfo() {
            try {
                const res = await fetch('https://api.binance.com/api/v3/exchangeInfo');
                if (!res.ok) throw new Error(`exchangeInfo error: ${res.status}`);
                const data = await res.json();
                const tradableUsdtBases = new Set(
                    data.symbols
                        .filter((s) => s.status === 'TRADING' && s.quoteAsset === 'USDT')
                        .map((s) => s.baseAsset.toUpperCase())
                );
                const desired = (requestedBases || []).map((b) => b.toUpperCase());
                const available = desired.filter((b) => tradableUsdtBases.has(b)).map((b) => `${b}USDT`);
                if (cancelled) return;
                setAvailableSymbols(available);
            } catch (_) {
                // swallow; no UI here to keep component minimal
            }
        }
        fetchExchangeInfo();
        return () => { cancelled = true; };
    }, [requestedBases]);

    useEffect(() => {
        if (!lowercaseStreams.length) return;
        const url = `wss://stream.binance.com:9443/stream?streams=${lowercaseStreams.join('/')}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onmessage = (evt) => {
            try {
                const payload = JSON.parse(evt.data);
                const t = payload && (payload.data || payload);
                if (!t || !t.s) return;
                const symbol = t.s;
                const lastPrice = t.c ? Number(t.c) : undefined;
                // push into fixed-size history for sparkline
                if (lastPrice !== undefined) {
                    const arr = priceHistoryRef.current[symbol] || [];
                    arr.push(lastPrice);
                    if (arr.length > 60) arr.shift(); // keep last 60 points
                    priceHistoryRef.current[symbol] = arr;
                }

                setTickerMap((prev) => ({
                    ...prev,
                    [symbol]: {
                        symbol,
                        lastPrice,
                        priceChangePercent: t.P ? Number(t.P) : undefined,
                    },
                }));
            } catch (_) { }
        };

        ws.onclose = () => {
            setTimeout(() => {
                setAvailableSymbols((prev) => [...prev]);
            }, 1500);
        };

        return () => {
            try { ws.close(); } catch (_) { }
        };
    }, [lowercaseStreams]);

    const items = useMemo(() => {
        return availableSymbols
            .map((sym) => tickerMap[sym])
            .filter(Boolean)
            .sort((a, b) => a.symbol.localeCompare(b.symbol));
    }, [availableSymbols, tickerMap]);

    const style = useMemo(() => ({
        '--ticker-duration': `${Math.max(6, Number(speedSeconds) || 40)}s`,
        '--ticker-direction': direction === 'right' ? 'reverse' : 'normal'
    }), [speedSeconds, direction]);

    function Sparkline({ symbol, colorClass }) {
        const points = priceHistoryRef.current[symbol] || [];
        if (!points.length) return <svg className="binance-ticker__sparkline" />;
        const width = 72, height = 28, pad = 2;
        const min = Math.min(...points);
        const max = Math.max(...points);
        const range = max - min || 1e-9;
        const step = (width - pad * 2) / Math.max(1, points.length - 1);
        const path = points
            .map((p, i) => {
                const x = pad + i * step;
                const y = height - pad - ((p - min) / range) * (height - pad * 2);
                return `${i === 0 ? 'M' : 'L'}${x},${y}`;
            })
            .join(' ');
        return (
            <svg className="binance-ticker__sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                <path className={`sparkline-path ${colorClass || 'neutral'}`} d={path} />
            </svg>
        );
    }

    return (
        <div className={`binance-ticker ${direction === 'right' ? 'ticker-reverse' : ''} ${offset ? 'ticker-offset' : ''}`} style={style}>
            <SkeletonTheme 
                // baseColor="var(--muted)" 
                // highlightColor="var(--border)"
                // borderRadius="6px"
            >
                <div className="binance-ticker__track" aria-hidden="true">
                {[...items, ...items].map((r, idx) => {
                    const baseSymbol = r.symbol.replace('USDT', '');
                    const coinIconUrl = coinIcons[baseSymbol] || 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png';

                    return (
                        <div key={`${r.symbol}-${idx}`} className="binance-ticker__item">
                            <div className="binance-ticker__coin-icon">
                                <img
                                    src={coinIconUrl}
                                    alt={baseSymbol}
                                    className="binance-ticker__coin-image"
                                    onError={(e) => {
                                        e.target.src = 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png';
                                    }}
                                />
                            </div>
                            <div>
                                <div className="binance-ticker__symbol">{r.symbol}</div>
                                <div className={`binance-ticker__price ${r.priceChangePercent > 0 ? 'green' : r.priceChangePercent < 0 ? 'red' : ''}`}>
                                    {r.lastPrice?.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                                </div>
                            </div>
                            <div className={`binance-ticker__stats-badge ${r.priceChangePercent > 0 ? 'badge-up' : r.priceChangePercent < 0 ? 'badge-down' : 'badge-neutral'}`}>
                                <div className="badge-sparkline-bg">
                                    <Sparkline 
                                        key={`sparkline-${r.symbol}-${r.lastPrice}-${idx}`}
                                        symbol={r.symbol} 
                                        colorClass={r.priceChangePercent > 0 ? 'green' : r.priceChangePercent < 0 ? 'red' : 'neutral'} 
                                    />
                                </div>
                                <div className="badge-content">
                                    <span className="badge-icon">{r.priceChangePercent > 0 ? '↑' : r.priceChangePercent < 0 ? '↓' : '•'}</span>
                                    <span className="badge-percent">{r.priceChangePercent?.toFixed(2)}%</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {!items.length && (
                    <>
                        {/* Skeleton Loader */}
                        {[...Array(8)].map((_, idx) => (
                            <div key={`skeleton-${idx}`} className="binance-ticker__item">
                                <div className="binance-ticker__coin-icon">
                                    <Skeleton circle height={20} width={20} />
                                </div>
                                <div className="binance-ticker__skeleton-content">
                                    <Skeleton height={14} width={60} />
                                    <Skeleton height={12} width={80} />
                                </div>
                                <Skeleton height={12} width={50} />
                                <Skeleton height={28} width={72} />
                            </div>
                        ))}
                    </>
                )}
                </div>
            </SkeletonTheme>
        </div>
    );
}


